import { useTranslation } from 'react-i18next'

// Derive status without moment
const deriveStatus = inv => {
	if (!inv) return 'issued'
	if (inv.status) return inv.status
	if (inv.paidDate) return 'paid'
	if (inv.paymentTerm && new Date() > new Date(inv.paymentTerm)) return 'overdue'
	return 'issued'
}

export default function InvoiceSummary({ invoice, totals, currencySymbol, className = 'flex lg:col-span-2 xl:col-span-1' }) {
	const { t } = useTranslation()
	const status = deriveStatus(invoice)
	const paymentTypeKeyMap = {
		card: 'card',
		bank_transfer: 'bankTransfer',
		bankTransfer: 'bankTransfer',
		cash: 'cash',
		other: 'other',
	}

	// --- helpers ---
	const dec = v => {
		if (v == null) return 0
		if (typeof v === 'object' && v.$numberDecimal !== undefined) {
			const n = Number(v.$numberDecimal)
			return Number.isFinite(n) ? n : 0
		}
		const n = Number(v)
		return Number.isFinite(n) ? n : 0
	}
	const fmtMoney = (n, currencySymbol) => `${currencySymbol} ${dec(n).toFixed(2)}`
	const fmtDate = d => (d ? new Intl.DateTimeFormat('en-GB').format(new Date(d)) : '—')
	const paymentType = paymentTypeKeyMap[invoice?.paymentType]

	return (
		<div className={className}>
			<div className="flex h-full w-full flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
				<div className="border-b border-gray-200 p-6">
					<h2 className="text-lg font-semibold text-gray-900">{t('invoices.details.summary.title')}</h2>
				</div>

				<div className="flex-1 space-y-4 p-6">
					{/* Dates & status */}
					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600">{t('invoices.form.dueDate')}</span>
						<span className="text-sm font-medium text-gray-900">
							{invoice?.dueDate ? fmtDate(invoice.dueDate) : '—'}
						</span>
					</div>

					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600">{t('invoices.form.payment.status')}</span>
						<span
							className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status === 'paid'
									? 'bg-green-100 text-green-800'
									: status === 'overdue'
										? 'bg-red-100 text-red-800'
										: 'bg-gray-100 text-gray-800'
								}`}>
							{t(`invoices.form.payment.statusOptions.${status}`, { defaultValue: status })}
						</span>
					</div>

					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600">{t('invoices.form.payment.paymentDate')}</span>
						<span className="text-sm font-medium text-gray-900">
							{invoice?.paidDate ? fmtDate(invoice.paidDate) : '-'}
						</span>
					</div>

					{invoice?.paymentType ? (
						<div className="flex items-center justify-between">
							<span className="text-sm text-gray-600">{t('invoices.form.payment.paymentType')}</span>
							<span className="text-sm font-medium text-gray-900">
								{paymentType
									? t(`invoices.form.payment.paymentTypes.${paymentType}`)
									: invoice.paymentType}
							</span>
						</div>
					) : null}

					<div className="h-px bg-gray-200 my-2" />

					{/* Totals */}
					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600">{t('invoices.form.totals.subtotal')}</span>
						<span className="text-sm font-medium text-gray-900">{fmtMoney(totals?.subtotal, currencySymbol)}</span>
					</div>


					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600">{t('invoices.form.totals.vatTotal')}</span>
						<span className="text-sm font-medium text-gray-900">{fmtMoney(totals?.taxTotal, currencySymbol)}</span>
					</div>

					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600">{t('invoices.form.discountPercent')}</span>
						<span className="text-sm font-medium text-gray-900">{totals?.discountPercent} %</span>
					</div>

					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600">{t('invoices.form.totals.total')}</span>
						<span className="text-lg font-semibold text-gray-900">{fmtMoney(totals?.total, currencySymbol)}</span>
					</div>

					{/* Notes */}
					{invoice?.publicNotes || invoice?.privateNotes ? (
						<>
							<div className="h-px bg-gray-200 my-2" />
							{invoice.publicNotes ? (
								<div>
									<div className="text-sm text-gray-600 mb-1">{t('invoices.form.notes.public')}</div>
									<div className="text-sm text-gray-800 whitespace-pre-wrap break-words">{invoice.publicNotes}</div>
								</div>
							) : null}
							{invoice.privateNotes ? (
								<div>
									<div className="text-sm text-gray-600 mb-1">{t('invoices.form.notes.private')}</div>
									<div className="text-sm text-gray-800 whitespace-pre-wrap break-words">{invoice.privateNotes}</div>
								</div>
							) : null}
						</>
					) : null}
				</div>
			</div>
		</div>
	)
}
